import { useEffect, useMemo, useState } from 'react'
import { type User } from '../../../models/User'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAllUserSharesQuery, useGetAllUsersQuery, useLazyGetAllUsersQuery } from '../../../store/user/user'
import { useLazyGetUnsubscribesQuery } from '../../../store/scheduling/scheduling'
import { MAX_UNSUBSCRIBE_LOOKUP_EMAILS, normalizeEmail } from '../../Scheduling/flows/useUnsubscribedClients'

const FETCH_CHUNK_SIZE = 500

const EMPTY_USERS: User[] = []

const EMPTY_CLIENT_IDS = new Set<string>()

const inChunks = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, (index + 1) * size))

export interface ClientOwnerGroups {
  ownerIds: string[]
  clientIdsWithoutOwner: string[]
  clientIdsWithUnsharedOwner: string[]
  ownerIdByClientId: Record<string, string | null>
}

export const groupClientsByOwner = (clients: User[], teammateIds: Set<string>, sharedUserIds: Set<string>): ClientOwnerGroups => {
  const ownerIdOf = (client: User): string | null => {
    const ownerId = client.owner?.ownerId
    return ownerId && teammateIds.has(ownerId) ? ownerId : null
  }
  return {
    ownerIds: Array.from(new Set(clients.map(ownerIdOf).filter((ownerId): ownerId is string => ownerId !== null))),
    clientIdsWithoutOwner: clients.filter((client) => ownerIdOf(client) === null).map((client) => client.id),
    clientIdsWithUnsharedOwner: clients
      .filter((client) => {
        const ownerId = ownerIdOf(client)
        return ownerId !== null && !sharedUserIds.has(ownerId)
      })
      .map((client) => client.id),
    ownerIdByClientId: Object.fromEntries(clients.map((client) => [client.id, ownerIdOf(client)])),
  }
}

interface ClientLookup {
  clientIds: string[]
  clients: User[]
  unsubscribedClientIds: Set<string>
  failed: boolean
}

export interface SendBookingInvitationsOwners extends ClientOwnerGroups {
  clients: User[]
  unsubscribedClientIds: Set<string>
  isLoading: boolean
  isError: boolean
  teammates: User[]
  sharedUserIds: Set<string>
}

export const useSendBookingInvitationsOwners = (clientIds: string[] | null): SendBookingInvitationsOwners => {
  const { accountId, id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()
  const [triggerGetUnsubscribes] = useLazyGetUnsubscribesQuery()
  const { data: teammatesResponse, isLoading: teammatesLoading, isError: teammatesError } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] })
  const {
    data: shares,
    isLoading: sharesLoading,
    isError: sharesError,
  } = useGetAllUserSharesQuery({ accountId, userId: currentUserId, shareType: 'scheduling' })
  const [lookup, setLookup] = useState<ClientLookup | null>(null)

  useEffect(() => {
    if (clientIds === null) {
      return
    }
    let ignore = false
    const findUnsubscribed = async (clients: User[]): Promise<Set<string>> => {
      const emails = Array.from(new Set(clients.flatMap((client) => (client.email ? [normalizeEmail(client.email)] : []))))
      const responses = await Promise.all(
        inChunks(emails, MAX_UNSUBSCRIBE_LOOKUP_EMAILS).map((chunk) => triggerGetUnsubscribes({ accountId, emails: chunk }, true).unwrap()),
      )
      const blocked = new Set(responses.flat().map((entry) => normalizeEmail(entry.email)))
      return new Set(clients.filter((client) => client.email && blocked.has(normalizeEmail(client.email))).map((client) => client.id))
    }
    const resolve = async (): Promise<Omit<ClientLookup, 'clientIds' | 'failed'>> => {
      const responses = await Promise.all(inChunks(clientIds, FETCH_CHUNK_SIZE).map((chunk) => triggerGetAllUsers({ accountId, ids: chunk }, true).unwrap()))
      const clients = responses.flatMap((response) => response?.data ?? [])
      return { clients, unsubscribedClientIds: await findUnsubscribed(clients) }
    }
    resolve()
      .then((resolved) => {
        if (!ignore) {
          setLookup({ clientIds, ...resolved, failed: false })
        }
      })
      .catch(() => {
        if (!ignore) {
          setLookup({ clientIds, clients: EMPTY_USERS, unsubscribedClientIds: EMPTY_CLIENT_IDS, failed: true })
        }
      })
    return () => {
      ignore = true
    }
  }, [accountId, clientIds, triggerGetAllUsers, triggerGetUnsubscribes])

  const currentLookup = clientIds !== null && lookup?.clientIds === clientIds ? lookup : null
  const clients = currentLookup && !currentLookup.failed ? currentLookup.clients : null
  const clientsError = currentLookup?.failed ?? false
  const teammates = teammatesResponse?.data ?? EMPTY_USERS
  const sharedUserIds = useMemo(() => new Set([currentUserId, ...(shares?.data ?? []).map((share) => share.ownerUserId)]), [currentUserId, shares])
  const groups = useMemo(
    () => groupClientsByOwner(clients ?? EMPTY_USERS, new Set(teammates.map((teammate) => teammate.id)), sharedUserIds),
    [clients, teammates, sharedUserIds],
  )

  return {
    ...groups,
    clients: clients ?? EMPTY_USERS,
    unsubscribedClientIds: currentLookup && !currentLookup.failed ? currentLookup.unsubscribedClientIds : EMPTY_CLIENT_IDS,
    teammates,
    sharedUserIds,
    isLoading: teammatesLoading || sharesLoading || (clients === null && !clientsError),
    isError: teammatesError || sharesError || clientsError,
  }
}
